/**
 * What a session card says the subthread under a post is doing (RFC 0302 S5).
 *
 * THREE STATES, and only three: working, waiting for you, done (3d). A card
 * that reported six would be a status panel, and the thing a person is scanning
 * for while reading a conversation is whether anything needs them.
 *
 * READ OFF THE REGISTRY, which is why this takes a row and not a session
 * document. Drawing a parent that fanned out six branches must not open six
 * session logs: the project document already carries `status`/`statusAt` per
 * session (RFC 0284 S8a) and that is a single read however many cards there
 * are. The cost of that choice is honest and deliberate — a session whose
 * runtime has not reported, or whose claim went stale, gets NO chip rather than
 * a guessed one. Nothing is worse here than a card that says done about work
 * still running.
 *
 * PURE, so the mapping is testable without rendering a thread.
 */

import { isSessionDone, type SessionRunStatus } from '@mod/core/model'

/** The three states, or nothing when the registry cannot say. */
export type SubthreadState = 'working' | 'waiting' | 'done'

export interface SubthreadRow {
  /** Fresh registry status, when there is one. Absent = nobody reported. */
  status?: SessionRunStatus
  /** When the session was filed as done, by a person or an agent (RFC 0286). */
  completedAt?: number
  sentAt?: number
}

export function subthreadState(
  row: SubthreadRow | undefined,
): SubthreadState | undefined {
  if (!row) return undefined
  // The same rule the session list uses (isSessionDone): filed, and nothing
  // since. A filed session that runs again is not done.
  if (isSessionDone(row)) return 'done'
  switch (row.status) {
    case 'asking':
      return 'waiting'
    case 'running':
    case 'queued':
      return 'working'
    // Idle is the end of a turn, which for a branch is the answer arriving.
    // It is also a session that never started — but a card is only drawn for
    // one a turn just opened, so the second reading does not arise here.
    case 'idle':
      return 'done'
    default:
      return undefined
  }
}

/** The word on the card. "Waiting for you" is the only one with emphasis, and
 *  the only one that is about the reader rather than the agent. */
export function subthreadLabel(state: SubthreadState): string {
  return state === 'waiting'
    ? 'waiting for you'
    : state === 'working'
      ? 'working'
      : 'done'
}
